import { formatDate, formatNumber, PercentPipe } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {
  Leg,
  ScheduleColumn,
  ScheduleData,
  ScheduleName,
  UserSettings
} from '@approot/models';
import { formattedDecimal } from '@approot/shared/directives/validators/input-validator.directive';
import { getAttributeControlFromKey } from '@approot/widgets/strategy-detail/leg/utilities/leg-attribute-utilities';
import { DateUtilities } from './date-utilities';
import { Utilities, validateNumber } from './utilities';
export default class ScheduleUtilities {
  static scheduleControlMaker(
    row: any,
    index: number,
    legForm: FormGroup,
    rfqLeg: boolean,
    productType: string,
    columns: ScheduleColumn[],
    legSchedule?: FormArray
  ) {
    // push a form group into, the array
    const scheduleGroup = new FormGroup({
      fixingNumber: new FormControl(index + 1), // this fixing number is only here, when i need to find out the index of a form group
      ...this.dates(row),
      ...this.notionals(row),
      ...this.strikes(row), // obtain all the different types of strikes, leverages or barriers a product schedule may contain
      ...this.leverages(row),
      ...this.barriers(row, legForm, rfqLeg, productType),
      ...this.lkos(row),
      ...this.otherTypes(row, legSchedule ? legSchedule.controls[index] : null)
    });

    Object.keys(scheduleGroup.controls).forEach(key => {
      if (legForm.get(key)?.disabled) {
        scheduleGroup.get(key).disable({ emitEvent: false });
      }

      const column = columns.find(column => column.shortName === key);

      if (!column?.format) {
        scheduleGroup.get(key).addValidators(formattedDecimal(true));
      }
    });
    scheduleGroup.get('fixingNumber').disable();
    return scheduleGroup;
  }

  static dates(fixing) {
    let dates = {};
    if ('fixingDate' in fixing) {
      dates['fixingDate'] = new FormControl(fixing.fixingDate, [
        Validators.required,
        this.dateValidator.bind(this),
        this.dateIncreasingValidator.bind(this)
      ]);
    }

    if ('expiryDate' in fixing) {
      dates['expiryDate'] = new FormControl(fixing.expiryDate, [
        Validators.required,
        this.dateValidator.bind(this),
        this.dateIncreasingValidator.bind(this)
      ]);
    }

    [
      'endDate',
      'payDate',
      'setDate',
      'startDate',
      'exerciseDate',
      'notificationDate'
    ].forEach(date => {
      if (date in fixing) {
        dates[date] = new FormControl(fixing[date], [
          Validators.required,
          this.dateValidator.bind(this)
        ]);
      }
    });

    if ('settlementDate' in fixing) {
      if (
        // this.data &&
        fixing.fixingDate === undefined &&
        fixing.expiryDate === undefined
      ) {
        // if no expiry or fixing date then we need to ensure that the settlement dates increases
        dates['settlementDate'] = new FormControl(fixing.settlementDate, [
          Validators.required,
          this.dateValidator.bind(this),
          this.dateIncreasingValidator.bind(this)
        ]);
      } else {
        dates['settlementDate'] = new FormControl(fixing.settlementDate, [
          Validators.required,
          this.dateValidator.bind(this),
          this.settlementDateGreaterValidator.bind(this)
        ]);
      }
    }
    return dates;
  }

  static notionals(fixing) {
    let notionals = {};

    [
      'notional',
      'secondNotional',
      'notionalLong',
      'notionalShort',
      'callNotional',
      'putNotional'
    ].forEach(notionalField => {
      if (notionalField in fixing) {
        notionals[notionalField] = new FormControl(fixing[notionalField], [
          this.notionalValidator.bind(this)
        ]);
      }
    });
    return notionals;
  }

  static otherTypes(fixing, scheduleRow: AbstractControl) {
    let other = {};

    if ('isExerciseDate' in fixing) {
      other['isExerciseDate'] = new FormControl(
        fixing.isExerciseDate === 'true' || fixing.isExerciseDate === true
          ? true
          : false,
        [Validators.required]
      );
    }

    if ('pivot' in fixing) {
      other['pivot'] = new FormControl(fixing.pivot, [Validators.required]);
    }

    if ('fixedLegRate' in fixing) {
      other['fixedLegRate'] = new FormControl(fixing.fixedLegRate, [
        Validators.required,
        this.percentRateValidator.bind(this)
      ]);
    }

    if ('floatingLegSpreadBps' in fixing) {
      other['floatingLegSpreadBps'] = new FormControl(
        fixing.floatingLegSpreadBps,
        [Validators.required]
      );
    }

    if ('fixing' in fixing) {
      // if there are fixings
      let disableFixing = false;
      let fixingDate; // the date is stored here
      if ('fixingDate' in fixing) {
        fixingDate = fixing.fixingDate;
      } else if ('expiryDate' in fixing) {
        fixingDate = fixing.expiryDate;
      } else if ('settlementDate' in fixing) {
        fixingDate = fixing.settlementDate;
      }

      disableFixing = new Date(fixingDate).getTime() > new Date().getTime();
      const dateValidators = [
        Validators.pattern(
          '^(0*[1-9][0-9]*(\\.[0-9]+)?|0+\\.[0-9]*[1-9][0-9]*)$'
        )
      ];
      other['fixing'] = new FormControl(
        fixing.fixing,
        disableFixing ? [] : dateValidators
      );
      if (disableFixing) {
        // if the fixing is in the past
        other['fixing'].disable({ emitEvent: false });
      } else if (scheduleRow) {
        scheduleRow.get('fixing').enable({ emitEvent: false });
        // enable the leg schedule since it maybe be valid now
        // as the date may have changed
        scheduleRow.get('fixing').setValidators(dateValidators);
      }
    }
    return other;
  }

  static barriers(
    fixing: Record<string, any>,
    legForm: FormGroup,
    rfqLeg: boolean,
    productType: string
  ): Record<string, FormControl> {
    // adds correct barriers required to the form
    let barriers: Record<string, FormControl> = {};
    ['barrier', 'secondBarrier', 'lowerBarrier', 'upperBarrier'].forEach(
      key => {
        if (key in fixing) {
          barriers[key] = new FormControl(fixing[key], [
            this.numberValidator.bind(this)
          ]);
        }
      }
    );

    [
      'rangeLowerBarrier',
      'rangeUpperBarrier',
      'kiBarrier',
      'koBarrier',
      'lkoLowerBarrier',
      'lkoUpperBarrier'
    ].forEach(key => {
      if (key in fixing) {
        barriers[key] = new FormControl(fixing[key]);
        if (productType === 'simpleaccumulator') {
          if (
            key === 'koBarrier' &&
            legForm.get('koBarrierType')?.value === 'AKO'
          ) {
            barriers[key].disable();
          }
          if (rfqLeg) {
            if (key === 'koBarrier') {
              (barriers[key] as FormControl).addValidators(
                koBarrierValidator.bind(barriers[key], legForm)
              );
            }

            if (key === 'kiBarrier') {
              (barriers[key] as FormControl).addValidators(
                kiBarrierValidator.bind(barriers[key], legForm)
              );
            }
          }
        }
      }
    });
    return barriers;
  }

  static strikes(fixing) {
    // adds the correct strikes required to the form
    let strikes = {};
    if ('strike' in fixing) {
      strikes['strike'] = new FormControl(fixing.strike, [
        Validators.required,
        this.numberValidator.bind(this)
      ]);
    }

    if ('secondStrike' in fixing) {
      strikes['secondStrike'] = new FormControl(fixing.secondStrike, [
        Validators.required,
        this.numberValidator.bind(this)
      ]);
    }

    if ('strikeShort' in fixing) {
      strikes['strikeShort'] = new FormControl(fixing.strikeShort, [
        Validators.required,
        this.numberValidator.bind(this)
      ]);
    }

    if ('strikeLong' in fixing) {
      strikes['strikeLong'] = new FormControl(fixing.strikeLong, [
        Validators.required,
        this.numberValidator.bind(this)
      ]);
    }

    if ('strikeInRange' in fixing) {
      strikes['strikeInRange'] = new FormControl(fixing.strikeInRange, []);
    }

    if ('callStrike' in fixing) {
      strikes['callStrike'] = new FormControl(fixing.callStrike, []);
    }

    if ('putStrike' in fixing) {
      strikes['putStrike'] = new FormControl(fixing.putStrike, []);
    }
    return strikes;
  }

  static leverages(fixing) {
    // adds the correct leverages required to the form
    let leverages = {};

    if ('leverage' in fixing) {
      leverages['leverage'] = new FormControl(fixing.leverage, [
        Validators.required,
        this.numberValidator.bind(this)
      ]);
    }

    if ('secondLeverage' in fixing) {
      leverages['secondLeverage'] = new FormControl(fixing.secondLeverage, [
        Validators.required,
        this.numberValidator.bind(this)
      ]);
    }

    if ('leverageShort' in fixing) {
      leverages['leverageShort'] = new FormControl(fixing.leverageShort, [
        Validators.required,
        this.numberValidator.bind(this)
      ]);
    }

    if ('leverageLong' in fixing) {
      leverages['leverageLong'] = new FormControl(fixing.leverageLong, [
        Validators.required,
        this.numberValidator.bind(this)
      ]);
    }

    if ('leverageLevel' in fixing) {
      leverages['leverageLevel'] = new FormControl(fixing.leverageLevel, []);
    }
    return leverages;
  }

  static lkos(fixing) {
    // adds the correct leverages required to the form
    let lkos = {};

    if ('lko' in fixing) {
      lkos['lko'] = new FormControl(fixing.lko, []);
    }

    if ('secondLko' in fixing) {
      lkos['secondLko'] = new FormControl(fixing.secondLko, []);
    }
    return lkos;
  }

  static dateValidator(control: AbstractControl): ValidationErrors | null {
    if (DateUtilities.isPrettyDate(control.value)) {
      return null;
    } else {
      return { invalidEntry: { value: control.value } };
    }
  }

  static formatValue(
    columnType: string,
    value: any,
    decimalPlaces: number,
    onBlur: boolean
  ): string {
    decimalPlaces = decimalPlaces || 0;
    switch (columnType) {
      case 'date':
        if (DateUtilities.isCorrectFullDateFormat(value)) {
          // only try to convert the date if, its of a correct format
          return formatDate(value, 'd MMM y', 'en');
        }
        break;
      case 'formattedNumber':
        if (value && validateNumber(value, true)) {
          if (!onBlur && Utilities.isNumberFormatted(value)) {
            return value;
          }
          return formatNumber(
            Utilities.formattedNumberToNumber(value),
            'en-GB',
            `1.${onBlur ? decimalPlaces : 0}-${decimalPlaces}`
          );
        }
        break;
      case 'formattedDecimal':
        return value.toFixed(decimalPlaces);
      case 'percentage':
        if (value.toString().charAt(value.length - 1) !== '%') {
          return value + '%';
        }
        break;
    }
    return value;
  }

  static settlementDateGreaterValidator(
    // ensures the settlement date is after the expiry/fixing date
    control: AbstractControl
  ): ValidationErrors | null {
    if (control.parent && control.parent.value) {
      const dateType =
        control.parent.value['fixingDate'] !== undefined
          ? 'fixingDate'
          : 'expiryDate'; // need to know what type of date we are going to be making a comparison to

      if (control.parent.value[dateType] !== undefined) {
        // we only want to ensure the settlement date is bigger that the expiry/fixingDate
        // if it exist. Some products only have a settlement date
        const dateToCompareWith = control.parent.value[dateType];
        if (new Date(control.value) < new Date(dateToCompareWith)) {
          return { invalidEntry: { value: control.value } };
        }
      }
    }
    return null;
  }

  // this ensures that the expiry date/ fixingDate are in increasing order
  static dateIncreasingValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    let dateFails = false; // stores whether the date falls before a previous date
    if (control.parent && control.parent.controls) {
      const fixingNumber = control.parent.controls['fixingNumber'].value;
      let dateType;
      if (
        control.parent.value['fixingDate'] === undefined &&
        control.parent.value['expiryDate'] === undefined
      ) {
        dateType = 'settlementDate';
      } else {
        dateType =
          control.parent.value['fixingDate'] !== undefined
            ? 'fixingDate'
            : 'expiryDate';
      } // need to know what type of date we are going to be using

      if (fixingNumber !== 0) {
        // if the fixing number isn't the first one then we need to ensure that this date is less than the previous
        const previousRows = control.parent.parent.value;
        for (let i = 0; i < fixingNumber - 1; i++) {
          if (new Date(control.value) <= new Date(previousRows[i][dateType])) {
            // if the date is before or equal the previous row date then the date fails the validation
            dateFails = true;
            break;
          }
        }
      }

      if (fixingNumber !== control.parent.parent.value.length) {
        const nextRow = control.parent.parent.controls[
          fixingNumber
        ] as FormGroup;
        if (
          dateFails ||
          !nextRow.get(dateType).valid ||
          new Date(control.value) >= new Date(nextRow.get(dateType).value)
        ) {
          // these checks are done to improve performance
          // we only want to carry out validation if the date failed, meaning that the others may be incorrect also.
          // if the next date is invalid we may change that state
          // or if the next date is smaller than the current date
          nextRow.get(dateType).updateValueAndValidity({ emitEvent: false });
        }
      }
    }

    if (dateFails) {
      // the date falls before a previous date
      return { invalidEntry: { value: control.value } };
    }
    return null;
  }

  static percentRateValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    if (/^-?\d+(\.\d+)?%$/.test(control.value)) {
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }

  static numberValidator(control: FormControl): ValidationErrors | null {
    return !control.value ||
      !isNaN(Utilities.cleanFormattedNumeric(control.value))
      ? null
      : { invalidEntry: { value: control.value } };
  }

  static notionalValidator(control: FormControl): ValidationErrors | null {
    if (typeof control.value == 'number') {
      // only way value can be a number is if it was set either by the
      // leg component when this component is initiated. Or its gone through the validation
      // the pipe will take care of formatting the data, which will not affect the underlying data
      // stored within the form
      return null;
    } else if (
      Utilities.isMillionShorthand(control.value) ||
      Utilities.isThousandShorthand(control.value)
    ) {
      control.setValue(Utilities.shortHandNumberConverter(control.value));
      return null;
    } else if (validateNumber(control.value)) {
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }
}

export function scheduleAttributeDate(
  attributeName: string,
  scheduleItemValue: string | number,
  scheduleRow: ScheduleData
) {
  if (scheduleItemValue) {
    scheduleRow[attributeName] = ScheduleUtilities.formatValue(
      'date',
      scheduleItemValue,
      null,
      false
    );
  }
}

export function scheduleAttributeString(
  attributeName: string,
  scheduleItemValue: string,
  scheduleRow: ScheduleData,
  column: ScheduleColumn,
  form: FormGroup,
  fieldNumber: string = ''
) {
  const ctrl = form.get(attributeName + fieldNumber) || form.get(attributeName);
  if (ctrl && scheduleItemValue === undefined) {
    const formValue = ctrl.value;
    const value =
      column.format === 'formattedNumber' && formValue
        ? Utilities.formattedNumberToNumber(formValue)
        : formValue;
    scheduleRow[attributeName] =
      formValue !== Utilities.customText
        ? ScheduleUtilities.formatValue(column.format, value, column.dp, false)
        : '';
  } else if (scheduleItemValue) {
    const value =
      column.format === 'formattedNumber' && scheduleItemValue
        ? Utilities.formattedNumberToNumber(scheduleItemValue)
        : scheduleItemValue;
    scheduleRow[attributeName] = ScheduleUtilities.formatValue(
      column.format,
      value,
      column.dp,
      false
    );
  }
}

export function firstDateToUseFn(form: FormGroup): string {
  if (form.get('firstFixingDate')) {
    return 'firstFixingDate';
  } else if (form.get('firstExpirationDate')) {
    return 'firstExpirationDate';
  } else if (form.get('firstSettlementDate')) {
    return 'firstSettlementDate';
  } else if (form.get('firstExerciseDate')) {
    return 'firstExerciseDate';
  } else {
    return '';
  }
}

export function lastDateToUseFn(form: FormGroup): string {
  if (form.get('lastFixingDate')) {
    return 'lastFixingDate';
  } else if (form.get('lastExpirationDate')) {
    return 'lastExpirationDate';
  } else if (form.get('lastSettlementDate')) {
    return 'lastSettlementDate';
  } else {
    return '';
  }
}

export function numberOfXToUseFn(form: FormGroup): string {
  if (form.get('nbFixings')) {
    return 'nbFixings';
  } else if (form.get('nbExpiries')) {
    return 'nbExpiries';
  } else if (form.get('nbSettlementDates')) {
    return 'nbSettlementDates';
  } else if (form.get('nbExerciseDates')) {
    return 'nbExerciseDates';
  } else {
    return '';
  }
}

export function frequencyToUseFn(form: FormGroup): string {
  if (form.get('frequency')) {
    return 'frequency';
  } else if (form.get('exerciseFrequency')) {
    return 'exerciseFrequency';
  } else {
    return '';
  }
}

export function clearLastFixingData(form: FormGroup, leg: Leg): void {
  // clears the last fixing date and the schedule

  const firstDateToUse = firstDateToUseFn(form);
  const lastDateToUse = lastDateToUseFn(form);
  const numberOfXToUse = numberOfXToUseFn(form);
  const frequencyToUse = frequencyToUseFn(form);

  if (
    form.get(firstDateToUse) &&
    form.get(numberOfXToUse) &&
    form.get(frequencyToUse)
  ) {
    // instead of rewriting the function select whether to use firstFixingDate, firstExpirationDate, firstSettlementDate
    // or nbFixings or nbExpires

    (form.get('schedule') as FormArray).clear(); // clear the array
    if (firstDateToUse) {
      form.get(firstDateToUse).setValue(''); // clear all the inputs related to the lastFixing
    }

    if (lastDateToUse) {
      form.get(lastDateToUse).setValue('');
    }

    if (numberOfXToUse) {
      form.get(numberOfXToUse).setValue('');
    }

    form.get(frequencyToUse).setValue('');
    const scheduleAttribute = getAttributeControlFromKey(
      'schedule',
      leg.attributesAsFormControls
    );

    if (scheduleAttribute) {
      scheduleAttribute.info = '';
      scheduleAttribute.imageSrc = '';
    }
  }
}

export function scheduleAttributeRate(
  attributeName: string,
  scheduleItemValue: number | string,
  scheduleRow: ScheduleData,
  form: FormGroup,
  percentPipe: PercentPipe,
  fieldNumber: string = ''
) {
  const formCtrl =
    form.get(attributeName + fieldNumber) || form.get(attributeName);
  if (formCtrl) {
    if (typeof scheduleItemValue === 'string') {
      if (scheduleItemValue.includes('%')) {
        // percentage value should contain a '%' if cloned
        scheduleRow[attributeName] = scheduleItemValue;
      }
      return;
    }

    if (
      formCtrl.value === Utilities.customText &&
      scheduleItemValue === undefined
    ) {
      scheduleRow[attributeName] = '';
    } else if (scheduleItemValue === undefined) {
      scheduleRow[attributeName] = formCtrl.value;
    } else if (scheduleItemValue !== undefined && !isNaN(scheduleItemValue)) {
      scheduleRow[attributeName] = percentPipe.transform(
        scheduleItemValue,
        '1.0-12'
      );
    }
  }
}

export function getScheduleAttributes(
  scheduleName: ScheduleName,
  leg: Leg,
  settings: UserSettings
): ScheduleColumn[] {
  const productType = leg.productType;
  // const settings = this.configService.userSettings();
  const product = settings.products.find(s => s.productName === productType);
  switch (scheduleName) {
    case 'schedule':
      return product.scheduleAttributes;
    case 'exerciseSchedule':
      return product.exerciseScheduleAttributes;
    case 'fixedLegSchedule':
      return product.fixedLegScheduleAttributes;
    case 'fixedLegSchedule1':
      return product.fixedLeg1ScheduleAttributes;
    case 'fixedLegSchedule2':
      return product.fixedLeg2ScheduleAttributes;
    case 'floatingLegSchedule':
      return product.floatingLegScheduleAttributes;
    default:
      return [];
  }
}

export function completeScheduleData(
  resp: ScheduleData[],
  scheduleName: ScheduleName,
  form: FormGroup,
  leg: Leg,
  isRfqLeg: boolean,
  percentPipe: PercentPipe,
  settings: UserSettings,
  fieldNumber: string = ''
) {
  (form.get(scheduleName) as FormArray).clear(); // clear the form array
  const columnsToUse = getScheduleAttributes(scheduleName, leg, settings);
  resp.forEach((scheduleItem: ScheduleData, index: number) => {
    const scheduleRow: ScheduleData = {};
    columnsToUse.forEach(column => {
      const columnShortName = column.shortName;
      const scheduleItemValue = scheduleItem[columnShortName];
      switch (column.format) {
        case 'date':
          scheduleAttributeDate(
            columnShortName,
            scheduleItemValue,
            scheduleRow
          );
          break;
        case 'percentage':
          scheduleAttributeRate(
            columnShortName,
            scheduleItemValue,
            scheduleRow,
            form,
            percentPipe,
            fieldNumber
          );
          break;
        default:
          scheduleAttributeString(
            columnShortName,
            scheduleItemValue,
            scheduleRow,
            column,
            form,
            fieldNumber
          );
          break;
      }
      if (scheduleRow[columnShortName] == null) {
        scheduleRow[columnShortName] = '';
      }
    });

    if (leg.productType === 'generictpf') {
      if (scheduleItem.isExerciseDate === undefined) {
        if (form.get('putCurrency')?.value) {
          scheduleItem.isExerciseDate = 'true';
        } else {
          scheduleItem.isExerciseDate = 'false';
        }
      }
    } else {
      delete scheduleItem.isExerciseDate;
    }

    (form.get(scheduleName) as FormArray).push(
      // push the generated form group
      ScheduleUtilities.scheduleControlMaker(
        // the object is converted into a form group
        scheduleRow,
        index,
        form,
        isRfqLeg,
        leg.productType,
        columnsToUse
      )
    );
  });
}

export function scheduleButtonName(scheduleName: string): string {
  switch (scheduleName) {
    case 'schedule':
      return 'Fixings';
    case 'fixedLegSchedule':
    case 'fixedLegSchedule1':
    case 'fixedLegSchedule2':
    case 'floatingLegSchedule':
      return 'Periods';
    case 'exerciseSchedule':
      return 'Exercises';
    default:
      return '';
  }
}

function koBarrierValidator(
  legForm: FormGroup,
  koBarrierCtrl: FormControl
): { invalidEntry: { value: string } } | null {
  const directionCtrl = legForm.get('direction');
  const koBarrierTypeCtrl = legForm.get('koBarrierType');
  const strikeCtrl = koBarrierCtrl.parent.get('strike');
  if (!directionCtrl || !koBarrierTypeCtrl || !strikeCtrl) {
    return null;
  }
  const rowStrike = Utilities.cleanFormattedNumeric(strikeCtrl.value);
  const koBarrier = Utilities.cleanFormattedNumeric(koBarrierCtrl.value);
  if (koBarrierTypeCtrl.value === 'EKO') {
    if (directionCtrl.value === 'Buy') {
      if (koBarrier < rowStrike) {
        return {
          invalidEntry: {
            value: `When the Knock out barrier type is EKO and the leg direction is Buy,
               the knock out barriers must be larger than the strike`
          }
        };
      }
    } else {
      if (koBarrier > rowStrike) {
        return {
          invalidEntry: {
            value: `When the Knock out barrier type is EKO and the leg direction is Sell,
              the knock out barriers must be less than the strike`
          }
        };
      }
    }
  }
  return null;
}

function kiBarrierValidator(
  legForm: FormGroup,
  kiBarrierCtrl: FormControl
): { invalidEntry: { value: string } } | null {
  const directionCtrl = legForm.get('direction');
  const rowStrikeCtrl = kiBarrierCtrl.parent.get('strike');

  if (
    !directionCtrl ||
    !rowStrikeCtrl ||
    !Utilities.isNotBlank(kiBarrierCtrl.value)
  ) {
    return null;
  }

  const rowStrike = Utilities.cleanFormattedNumeric(rowStrikeCtrl.value);
  const kiBarrier = Utilities.cleanFormattedNumeric(kiBarrierCtrl.value);

  if (directionCtrl.value === 'Buy') {
    if (kiBarrier >= rowStrike) {
      return {
        invalidEntry: {
          value: `When the Knock out barrier type is EKO and the direction is buy,
            the knock out barriers but be larger than the strike`
        }
      };
    }
  } else {
    if (kiBarrier <= rowStrike) {
      return {
        invalidEntry: {
          value: `When the Knock out barrier type is EKO and the direction,
              the knock out barriers must be less than the strike`
        }
      };
    }
  }

  return null;
}
